import React, { useEffect } from 'react';
import { Button } from '@sato/components';
import { observer } from 'mobx-react-lite';

import { IWidget, ParsedEntry } from '@/contentful/types';
import { linkButtonVariantMap } from '@/contentful/utils/entry.utils';

import { Link } from '@components/common/Link';
import { useCustomTranslations } from '@hooks/useCustomTranslations';
import { useSwitchApartment } from '@stores/apartmentStore';
import { contractStore } from '@stores/contractStore';

import { parseHomeChangerSwitchButtonParameters } from './HomeChangerSwitchButton.utils';

/**
 * For a home changer, a button that switches to the apartment they are moving to or from, e.g. from the
 * terminated apartment to the new one, and optionally navigates to a URL, e.g. moving-in instructions.
 * Hidden if there is no such apartment. Defined as a widget in Contentful.
 */
export const HomeChangerSwitchButton = observer(
  ({ parameters, customTranslations }: ParsedEntry<IWidget>) => {
    const { t } = useCustomTranslations(customTranslations);
    const switchApartment = useSwitchApartment();
    const otherApartmentId = contractStore.otherApartmentIdInHomeChange;

    const { hasContracts } = contractStore;

    useEffect(() => {
      if (hasContracts) contractStore.fetchOtherApartmentContracts();
    }, [hasContracts]);

    const { url, variant, icon, iconPosition, className } =
      parseHomeChangerSwitchButtonParameters(parameters);

    const text = t('text');

    if (!text || !otherApartmentId) return null;

    const buttonProps = {
      variant: linkButtonVariantMap[variant ?? 'no variant'],
      icon,
      iconPosition,
      className,
      onClick: () => switchApartment(otherApartmentId),
      children: text,
    };

    return url ? (
      <Button wrapperComponent={Link} to={url} {...buttonProps} />
    ) : (
      <Button {...buttonProps} />
    );
  }
);
